import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Pill } from '../../components/board'
import { labState, packLotState } from '../../model/packing'

/** A white board card with a bold heading ("1 · Product (SKU)", "Run record"). */
export function BoardCard({
  title,
  aside,
  children,
  className,
}: {
  title?: ReactNode
  aside?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'flex flex-col gap-2.5 rounded-card border-[1.5px] border-rule bg-surface p-4',
        className,
      )}
    >
      {(title || aside) && (
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          {title && <h2 className="text-[17px] font-bold leading-tight">{title}</h2>}
          {aside}
        </div>
      )}
      {children}
    </section>
  )
}

/** Label left, value right, in a two-column grid (the record pages' fact rows). */
export function RecordGrid({
  rows,
}: {
  rows: Array<{ label: ReactNode; value: ReactNode; strong?: boolean } | null | false>
}) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-[15px] sm:grid-cols-[minmax(0,1fr)_auto]">
      {rows.filter(Boolean).map((r, i) => {
        const row = r as { label: ReactNode; value: ReactNode; strong?: boolean }
        return (
          <div key={i} className="contents">
            <dt className="text-muted">{row.label}</dt>
            <dd className={cn('tabular sm:text-right', row.strong && 'font-bold')}>{row.value}</dd>
          </div>
        )
      })}
    </dl>
  )
}

export function PackLotPill({ status, hold }: { status: string; hold: string }) {
  const s = packLotState(status, hold)
  return <Pill tone={s.tone}>{s.text}</Pill>
}

export function LabPill({ overall }: { overall: string | null | undefined }) {
  const s = labState(overall)
  return <Pill tone={s.tone}>{s.text}</Pill>
}

/** Small "Field label / control" pair used in the board forms. */
export function BoardField({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-[15px]">
      <span className="font-bold">{label}</span>
      {children}
      {hint && !error && <span className="text-[13px] text-muted">{hint}</span>}
      {error && <span className="text-[13px] text-danger">{error}</span>}
    </label>
  )
}
